'use client';

import { CoreEvent, eventNames } from '@carvana/core-events';
import { useReducedMotion } from 'motion/react';
import { useCallback, useEffect, useState } from 'react';
import useComposerOpenReady from '~/hooks/common/composer/useComposerOpenReady';
import { TYPE_TICK_MS } from '~/hooks/home/useComposerStarterQuestionRotation';

type ReturnType = {
  displayText: string | null;
  isTyping: boolean;
  startTyping: (question: string) => void;
};

export default function useTypeQuestionThenOpenComposer(): ReturnType {
  const prefersReducedMotion = useReducedMotion();
  const { whenReady } = useComposerOpenReady();
  const [question, setQuestion] = useState<string | null>(null);
  const [charCount, setCharCount] = useState(0);

  const startTyping = useCallback(
    (next: string) => {
      setQuestion(next);
      setCharCount(prefersReducedMotion ? next.length : 0);
    },
    [prefersReducedMotion]
  );

  useEffect(() => {
    if (question === null) return undefined;

    if (charCount >= question.length) {
      whenReady(() => {
        CoreEvent.emit(eventNames.composer.open, { question, source: 'page' });
      });
      setQuestion(null);
      setCharCount(0);

      return undefined;
    }

    const timeoutId = setTimeout(
      () => setCharCount(previous => previous + 1),
      TYPE_TICK_MS
    );

    return () => clearTimeout(timeoutId);
  }, [charCount, question, whenReady]);

  return {
    displayText: question === null ? null : question.slice(0, charCount),
    isTyping: question !== null,
    startTyping
  };
}
